import { config } from "../models/config";
import { jsonHeaders } from "./apiHeaders";
import type { GetToken } from "./accountApi";
import { ApiError, friendlyMessage } from "./accountApi";
import type {
  AddIssueCommentRequest,
  AddPostCommentRequest,
  BurndownChart,
  ChatChannel,
  ChatMember,
  ChatMessage,
  CreateGroupChannelRequest,
  CreateIssueRequest,
  CreatePostRequest,
  CreateSprintRequest,
  Issue,
  IssueActivity,
  IssueComment,
  IssuePriority,
  IssueStatus,
  IssueType,
  MyActivity,
  Notification,
  OpenDirectChannelRequest,
  Post,
  PostComment,
  SendMessageRequest,
  Sprint,
  StartSprintRequest,
  UpdateIssueRequest,
  UpdatePostRequest,
  UpdateSprintRequest,
} from "../models/project";

export type { GetToken };
export { ApiError, friendlyMessage };

// Typed client for the project-subsystems REST surface (issues/sprints/board, blog, chat, cross-
// project activity). Same bearer-fetch pattern as accountApi.ts; reuses its ApiError/friendlyMessage.
async function call<T>(getToken: GetToken, method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.apiBase}${path}`, {
    method,
    headers: jsonHeaders(getToken),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json().catch(() => undefined as T);
}

// ---- Issues ----
export const createIssue = (getToken: GetToken, projectId: string, req: CreateIssueRequest) =>
  call<Issue>(getToken, "POST", `/api/projects/${projectId}/issues`, req);

export const listIssues = (getToken: GetToken, projectId: string, workArea?: string) =>
  call<Issue[]>(getToken, "GET",
    `/api/projects/${projectId}/issues${workArea ? `?workArea=${encodeURIComponent(workArea)}` : ""}`);

export const getIssue = (getToken: GetToken, id: string) =>
  call<Issue>(getToken, "GET", `/api/issues/${id}`);

export const updateIssue = (getToken: GetToken, id: string, req: UpdateIssueRequest) =>
  call<Issue>(getToken, "PATCH", `/api/issues/${id}`, req);

export const deleteIssue = (getToken: GetToken, id: string) =>
  call<void>(getToken, "DELETE", `/api/issues/${id}`);

export const changeIssueStatus = (getToken: GetToken, id: string, status: IssueStatus) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/status`, { status });

export const assignIssue = (getToken: GetToken, id: string, assigneeId: string | null) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/assignee`, { assigneeId });

export const changeIssueType = (getToken: GetToken, id: string, type: IssueType) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/type`, { type });

export const setIssuePoints = (getToken: GetToken, id: string, storyPoints: number | null) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/points`, { storyPoints });

export const moveIssueToSprint = (getToken: GetToken, id: string, sprintId: string | null) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/sprint`, { sprintId });

export const setIssueLabels = (getToken: GetToken, id: string, labels: string[]) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/labels`, { labels });

export const setIssueRank = (getToken: GetToken, id: string, rank: string) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/rank`, { rank });

export const setIssueWorkAreas = (getToken: GetToken, id: string, workAreaTypes: string[]) =>
  call<Issue>(getToken, "PUT", `/api/issues/${id}/work-areas`, { workAreaTypes });

export const watchIssue = (getToken: GetToken, id: string, watch: boolean) =>
  call<Issue>(getToken, "POST", `/api/issues/${id}/watch`, { watch });

export const listSubtasks = (getToken: GetToken, id: string) =>
  call<Issue[]>(getToken, "GET", `/api/issues/${id}/subtasks`);

export const listIssueComments = (getToken: GetToken, id: string) =>
  call<IssueComment[]>(getToken, "GET", `/api/issues/${id}/comments`);

export const addIssueComment = (getToken: GetToken, id: string, req: AddIssueCommentRequest) =>
  call<IssueComment>(getToken, "POST", `/api/issues/${id}/comments`, req);

export const issueActivity = (getToken: GetToken, id: string) =>
  call<IssueActivity[]>(getToken, "GET", `/api/issues/${id}/activity`);

export const issuePriorities: IssuePriority[] = ["HIGHEST", "HIGH", "MEDIUM", "LOW", "LOWEST"];
export const issueTypes: IssueType[] = ["EPIC", "STORY", "TASK", "BUG", "SUBTASK"];
export const issueStatuses: IssueStatus[] = ["BACKLOG", "TODO", "IN_PROGRESS", "IN_REVIEW", "DONE"];

// ---- Sprints ----
export const createSprint = (getToken: GetToken, projectId: string, req: CreateSprintRequest) =>
  call<Sprint>(getToken, "POST", `/api/projects/${projectId}/sprints`, req);

export const listSprints = (getToken: GetToken, projectId: string) =>
  call<Sprint[]>(getToken, "GET", `/api/projects/${projectId}/sprints`);

export const getSprint = (getToken: GetToken, id: string) =>
  call<Sprint>(getToken, "GET", `/api/sprints/${id}`);

export const updateSprint = (getToken: GetToken, id: string, req: UpdateSprintRequest) =>
  call<Sprint>(getToken, "PATCH", `/api/sprints/${id}`, req);

export const deleteSprint = (getToken: GetToken, id: string) =>
  call<void>(getToken, "DELETE", `/api/sprints/${id}`);

export const startSprint = (getToken: GetToken, id: string, req: StartSprintRequest = {}) =>
  call<Sprint>(getToken, "POST", `/api/sprints/${id}/start`, req);

export const completeSprint = (getToken: GetToken, id: string) =>
  call<Sprint>(getToken, "POST", `/api/sprints/${id}/complete`);

export const sprintBurndown = (getToken: GetToken, id: string) =>
  call<BurndownChart>(getToken, "GET", `/api/sprints/${id}/burndown`);

// ---- Board ----
export const kanbanBoard = (getToken: GetToken, projectId: string) =>
  call<Issue[]>(getToken, "GET", `/api/projects/${projectId}/board/kanban`);

export const activeSprintBoard = (getToken: GetToken, projectId: string) =>
  call<Issue[]>(getToken, "GET", `/api/projects/${projectId}/board/active-sprint`);

export const backlog = (getToken: GetToken, projectId: string) =>
  call<Issue[]>(getToken, "GET", `/api/projects/${projectId}/backlog`);

// ---- Blog ----
export const createPost = (getToken: GetToken, projectId: string, req: CreatePostRequest) =>
  call<Post>(getToken, "POST", `/api/projects/${projectId}/posts`, req);

export const listPosts = (getToken: GetToken, projectId: string) =>
  call<Post[]>(getToken, "GET", `/api/projects/${projectId}/posts`);

export const getPost = (getToken: GetToken, id: string) =>
  call<Post>(getToken, "GET", `/api/posts/${id}`);

export const updatePost = (getToken: GetToken, id: string, req: UpdatePostRequest) =>
  call<Post>(getToken, "PATCH", `/api/posts/${id}`, req);

export const deletePost = (getToken: GetToken, id: string) =>
  call<void>(getToken, "DELETE", `/api/posts/${id}`);

export const publishPost = (getToken: GetToken, id: string) =>
  call<Post>(getToken, "POST", `/api/posts/${id}/publish`);

export const archivePost = (getToken: GetToken, id: string) =>
  call<Post>(getToken, "POST", `/api/posts/${id}/archive`);

export const listPostComments = (getToken: GetToken, id: string) =>
  call<PostComment[]>(getToken, "GET", `/api/posts/${id}/comments`);

export const addPostComment = (getToken: GetToken, id: string, req: AddPostCommentRequest) =>
  call<PostComment>(getToken, "POST", `/api/posts/${id}/comments`, req);

export const deletePostComment = (getToken: GetToken, commentId: string) =>
  call<void>(getToken, "DELETE", `/api/posts/comments/${commentId}`);

// ---- Chat ----
export const listChatChannels = (getToken: GetToken, projectId: string) =>
  call<ChatChannel[]>(getToken, "GET", `/api/projects/${projectId}/chat/channels`);

export const openDirectChannel = (getToken: GetToken, projectId: string, req: OpenDirectChannelRequest) =>
  call<ChatChannel>(getToken, "POST", `/api/projects/${projectId}/chat/direct`, req);

export const createGroupChannel = (getToken: GetToken, projectId: string, req: CreateGroupChannelRequest) =>
  call<ChatChannel>(getToken, "POST", `/api/projects/${projectId}/chat/groups`, req);

export const listWorkspaceChatChannels = (getToken: GetToken, workspaceId: string) =>
  call<ChatChannel[]>(getToken, "GET", `/api/workspaces/${workspaceId}/chat/channels`);

export const openWorkspaceDirectChannel = (getToken: GetToken, workspaceId: string, req: OpenDirectChannelRequest) =>
  call<ChatChannel>(getToken, "POST", `/api/workspaces/${workspaceId}/chat/direct`, req);

export const createWorkspaceGroupChannel = (getToken: GetToken, workspaceId: string, req: CreateGroupChannelRequest) =>
  call<ChatChannel>(getToken, "POST", `/api/workspaces/${workspaceId}/chat/groups`, req);

export const getChatChannel = (getToken: GetToken, id: string) =>
  call<ChatChannel>(getToken, "GET", `/api/chat/channels/${id}`);

export const listChatMembers = (getToken: GetToken, id: string) =>
  call<ChatMember[]>(getToken, "GET", `/api/chat/channels/${id}/members`);

export const addChatMember = (getToken: GetToken, id: string, userId: string) =>
  call<ChatMember>(getToken, "POST", `/api/chat/channels/${id}/members`, { userId });

export const listChatMessages = (getToken: GetToken, id: string) =>
  call<ChatMessage[]>(getToken, "GET", `/api/chat/channels/${id}/messages`);

export const sendChatMessage = (getToken: GetToken, id: string, req: SendMessageRequest) =>
  call<ChatMessage>(getToken, "POST", `/api/chat/channels/${id}/messages`, req);

export const markChannelRead = (getToken: GetToken, id: string) =>
  call<void>(getToken, "POST", `/api/chat/channels/${id}/read`);

// ---- Cross-project activity ----
export const getMyActivity = (getToken: GetToken) =>
  call<MyActivity>(getToken, "GET", "/api/account/activity");

export const listNotifications = (getToken: GetToken) =>
  call<Notification[]>(getToken, "GET", "/api/account/notifications");

export const markNotificationRead = (getToken: GetToken, id: string) =>
  call<void>(getToken, "POST", `/api/account/notifications/${id}/read`);

export const markAllNotificationsRead = (getToken: GetToken) =>
  call<void>(getToken, "POST", "/api/account/notifications/read-all");
